import type { UserInfo } from "@/types/auth";
import { logger } from "./logger";

/**
 * Simple JWT decoder for reading claims (NOT for validation)
 *
 * ⚠️ SECURITY WARNING ⚠️
 * This function does NOT validate JWT signatures and should NEVER be used
 * for authorization or security decisions. The decoded claims can be
 * manipulated by an attacker and must be treated as UNTRUSTED.
 *
 * Use ONLY for:
 * - Display purposes (showing user email in UI)
 * - Debug logging and observability
 *
 * All authorization MUST happen on the backend with proper JWT validation.
 *
 * JWTs are formatted as: header.payload.signature
 * We only need to decode the payload to read user information for debugging.
 */

export interface JWTPayload {
  sub?: string; // User ID
  email?: string;
  name?: string;
  display_name?: string;
  is_admin?: boolean; // Admin flag from AIEW session (Discord admin verification)
  entitlements?: string[];
  tier?: string;
  max_trial?: boolean; // Temporary Max-tier boost (Trader Max Boost purchase)
  boost_eligible?: boolean; // User's plan/cohort is eligible for Trader Max Boost
  exp?: number; // Expiration timestamp
  iat?: number; // Issued at timestamp
  has_accepted_current_tos?: boolean; // ToS acceptance status from AIE (snake_case to match AIE backend)
  [key: string]: unknown; // Other claims
}

/**
 * Decode a JWT token to read its payload claims
 *
 * ⚠️ SECURITY WARNING ⚠️
 * This does NOT validate the signature - it's only for reading claims for
 * display and debugging purposes. Never use for authorization decisions.
 */
export function decodeJWT(token: string): JWTPayload | null {
  // Input validation
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    if (!payload) {
      return null;
    }

    // Base64URL decode - replace chars and pad if needed
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

    // Validate base64 string before decoding
    if (!/^[A-Za-z0-9+/]*={0,2}$/.test(paddedBase64)) {
      return null;
    }

    // Decode base64 to JSON
    const jsonPayload = atob(paddedBase64);
    const parsed = JSON.parse(jsonPayload);

    // Validate that the parsed result is an object (not array, null, etc.)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as JWTPayload;
    }
    return null;
  } catch (error) {
    logger.error('Failed to decode JWT', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 */
export function isJWTExpired(token: string): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  return payload.exp * 1000 < Date.now();
}

/**
 * Extract user information from a JWT token for display and debugging
 *
 * ⚠️ SECURITY WARNING ⚠️
 * The returned user info is NOT validated and should NEVER be used for
 * authorization decisions. Use only for display and debugging.
 */
export function extractUserInfo(token: string): UserInfo | null {
  const payload = decodeJWT(token);
  if (!payload) {
    return null;
  }

  // Extract display_name and name, treating null as undefined for type compatibility
  const displayName =
    typeof payload.display_name === "string" ? payload.display_name : undefined;
  const name = typeof payload.name === "string" ? payload.name : undefined;

  return {
    ...(payload.sub !== undefined && { userId: payload.sub }),
    ...(payload.email !== undefined && { email: payload.email }),
    ...(displayName !== undefined && { displayName }),
    ...(name !== undefined && { name }),
    // Preserve legacy name usage by falling back to display_name if name is absent
    ...(name === undefined && displayName !== undefined && { name: displayName }),
    isAdmin: payload.is_admin === true,
    subscriptionTier: typeof payload.tier === "string" ? payload.tier : undefined,
    maxTrialActive: payload.max_trial === true,
    boostEligible: payload.boost_eligible === true,
  };
}
